import { HttpStatusCode } from "@angular/common/http";
import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from "@angular/core";
import { FormControl } from "@angular/forms";
import { NgxSpinnerService } from "ngx-spinner";
import {
  debounceTime,
  distinctUntilChanged,
  forkJoin,
  Observable,
  Subscription,
  switchMap,
  tap,
} from "rxjs";
import {
  CompanyDTO,
  CompetencyDTO,
  CompetencyService,
  PageProjectTableDTO,
  ProjectService,
  ProjectTableDTO,
} from "src/shared/api/generated";
import {
  NgbdSortableHeader,
  SortEvent,
} from "src/shared/directives/ngbd-sortable-header.directive";
import { showLoadingSpinner } from "src/shared/operators/loading-spinner.operator";
import { CompetencyManagerService } from "src/shared/services/competency-manager.service";
import { ConfirmationModalService } from "src/shared/services/confirmation-modal.service";
import { PermissionService } from "src/shared/services/permission.service";

@Component({
  selector: "app-table-project",
  templateUrl: "./table-project.component.html",
  styleUrls: ["./table-project.component.scss"],
})
export class TableProjectComponent implements OnInit, OnDestroy {
  @Input() company: CompanyDTO = {} as CompanyDTO;

  @ViewChildren(NgbdSortableHeader)
  headers = new QueryList<NgbdSortableHeader>();

  isAdmin: boolean = false;
  projects: ProjectTableDTO[] = [];
  page = 1;
  pageSize = 15;
  collectionSize = 0;
  sorting: SortEvent = { column: "name", direction: "asc" };
  searchText = new FormControl("");
  selectedRow: number = -1;
  search: boolean = false;
  competencies: Map<number, string> = new Map();
  displayedProjects: string = "all";

  timeoutId: any = null;

  createProjectMode: boolean = false;
  cloneProjectMode: boolean = false;
  projectIdToClone: number = -1;
  subscriptions: Subscription = new Subscription();

  constructor(
    private competencyService: CompetencyService,
    private projectService: ProjectService,
    private spinnerService: NgxSpinnerService,
    private competencyManagerService: CompetencyManagerService,
    private permissionService: PermissionService,
    private confirmationModalService: ConfirmationModalService
  ) {}

  ngOnInit(): void {
    this.isAdmin = this.permissionService.checkAdmin();
    this.initialLoad();
    this.subscriptions.add(
      this.competencyManagerService.competencyUpdated.subscribe((_) => {
        this.getAllCompetencies();
      })
    );
    this.subscriptions.add(
      this.competencyManagerService.competencyDeleted.subscribe((_) => {
        this.refreshProjectsAndCompetencies();
      })
    );
    this.searchProjects();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.cancelCloneProject();
  }

  getProjectsForCurrentCompany(
    searchTerm: string = ""
  ): Observable<PageProjectTableDTO> {
    if (this.cloneProjectMode) {
      this.cancelCloneProject();
    }
    if (this.createProjectMode) {
      this.cancelCreateProject();
    }

    if (this.displayedProjects === "active") {
      return this.projectService.getActiveProjectsByCompanyId(
        this.company.id!,
        searchTerm.trim(),
        this.pageSize,
        this.page - 1,
        this.sorting.column,
        this.sorting.direction
      );
    }
    return this.projectService.getAllProjectsByCompanyId(
      this.company.id!,
      searchTerm.trim(),
      this.pageSize,
      this.page - 1,
      this.sorting.column,
      this.sorting.direction
    );
  }

  getAllCompetenciesObservable(): Observable<Array<CompetencyDTO>> {
    return this.competencyService.getAllCompetencies();
  }

  getAllCompetencies(): void {
    this.getAllCompetenciesObservable().subscribe((result) => {
      result.forEach((competency: CompetencyDTO) => {
        this.competencies.set(competency.id!, competency.name);
      });
    });
  }

  initialLoad(): void {
    this.refreshProjectsAndCompetencies();
  }

  refreshProjectsAndCompetencies(): void {
    forkJoin([
      this.getAllCompetenciesObservable(),
      this.getProjectsForCurrentCompany(this.searchText.value ?? ""),
    ])
      .pipe(showLoadingSpinner(this.spinnerService))
      .subscribe(([competencies, projects]) => {
        competencies.forEach((competency: CompetencyDTO) => {
          this.competencies.set(competency.id!, competency.name);
        });
        this.projects = projects.content ?? [];
        this.collectionSize = projects.totalElements ?? 0;
        this.selectedRow = -1;
      });
  }

  onSort({ column, direction }: SortEvent) {
    this.headers.forEach((header) => {
      if (header.sortable !== column) {
        header.direction = "";
      }
    });

    this.sorting = { column: column, direction: direction };
    this.refreshProjects();
  }

  refreshProjects() {
    this.getProjectsForCurrentCompany(this.searchText.value ?? "")
      .pipe(showLoadingSpinner(this.spinnerService))
      .subscribe((projectPage) => {
        this.projects = projectPage.content ?? [];
        this.collectionSize = projectPage.totalElements ?? 0;
        this.selectedRow = -1;
      });
  }

  toggleDisplayMode() {
    this.page = 1;
    this.refreshProjects();
    this.competencyManagerService.clearProjectCompetency();
  }

  searchProjects() {
    this.searchText.valueChanges
      .pipe(
        tap(() => (this.search = false)),
        debounceTime(500),
        distinctUntilChanged(),
        tap(() => {
          this.competencyManagerService.clearProjectCompetency();
        }),
        switchMap((text) =>
          this.getProjectsForCurrentCompany(text ?? "").pipe(
            showLoadingSpinner(this.spinnerService)
          )
        )
      )
      .subscribe((projectPage) => {
        this.projects = projectPage.content ?? [];
        this.collectionSize = projectPage.totalElements ?? 0;
        this.selectedRow = -1;
        this.search = true;
      });
  }

  addEmptyProject() {
    this.projects.unshift({
      id: undefined,
      name: "",
      start: "",
      end: "",
      internal: true,
      projectState: "PLANNED",
      fte: 0,
      companyId: this.company.id!,
      competencies: undefined,
      members: [],
      alternativeName: undefined,
      description: undefined,
    });
    this.collectionSize++;
    this.createProjectMode = true;
  }

  createProject() {
    if (this.isCreateDisabled()) return;

    const projectToSave = this.projects.find((project) => !project.id)!;
    this.projectService
      .createProject(projectToSave)
      .pipe(showLoadingSpinner(this.spinnerService))
      .subscribe({
        next: (createdProject) => {
          const index = this.projects.findIndex((project) => !project.id);
          this.projects[index] = { ...createdProject };
          this.createProjectMode = false;
        },
        error: (error) => {
          if (error.status === HttpStatusCode.Conflict) {
            this.confirmationModalService.openConfirmationModal(
              "validation_message.error.title",
              "project.error.duplicate_name",
              true
            );
          } else if (error.status === HttpStatusCode.BadRequest) {
            this.confirmationModalService.openConfirmationModal(
              "validation_message.error.title",
              error.error.message,
              true
            );
          }
        },
      });
  }

  cancelCreateProject() {
    this.collectionSize--;
    this.createProjectMode = false;
    this.projects = this.projects.filter((project) => project.id);
  }

  isCreateDisabled() {
    const createdProject = this.projects.find((project) => !project.id);
    return (
      !createdProject?.name.trim()
    );
  }

  selectRow(project: ProjectTableDTO) {
    if (this.createProjectMode || this.cloneProjectMode || !project.id) {
      return;
    }
    if (this.selectedRow === project.id) {
      this.selectedRow = -1;
    } else {
      this.selectedRow = project.id;
    }
    this.competencyManagerService.setProjectCompetency(
      false,
      project.id,
      project.name,
      project.competencies ?? []
    );
  }

  openDeleteConfirmationModal(id: number) {
    this.confirmationModalService
      .openConfirmationModal(
        "project.confirm_delete",
        "project.delete_dialog_message"
      )
      .then(
        (result) => {
          if (result) {
            this.deleteProject(id);
          }
        },
        () => {}
      );
  }

  deleteProject(id: number) {
    this.projectService.deleteProject(id).subscribe({
      next: () => {
        this.refreshProjects();
      },
    });
  }

  cloneProject(id: number) {
    this.projectService.cloneProject(id).subscribe({
      next: (cloneProject) => {
        this.projects.unshift(cloneProject);
        this.collectionSize++;
        this.projectIdToClone = id;
        this.cloneProjectMode = true;
      },
    });
  }

  cancelCloneProject() {
    this.collectionSize--;
    this.projects = this.projects.filter((project) => project.id);
    this.cloneProjectMode = false;
    this.projectIdToClone = -1;
  }

  saveClonedProject() {
    this.projectService
      .saveProjectClone(
        this.projectIdToClone,
        this.projects.find((project) => !project.id)!
      )
      .pipe(showLoadingSpinner(this.spinnerService))
      .subscribe({
        next: (cloneProject) => {
          let index = this.projects.findIndex((project) => !project.id);
          this.projects[index] = { ...cloneProject };
          this.cloneProjectMode = false;
          this.projectIdToClone = -1;
        },
        error: (error) => {
          if (error.status === HttpStatusCode.Conflict) {
            this.confirmationModalService.openConfirmationModal(
              "project.error.title",
              "project.error.duplicate_name",
              true
            );
          }
        },
      });
  }
}
